import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material";
import { ConfirmAlert } from "../Alerts/Alerts";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { useTranslationApp } from "../../lib/i18next";
import {
  BUTTON_CANCEL,
  MODAL_CLOSE_UNSAVED_CONFIRM,
  MODAL_CLOSE_UNSAVED_WARNING_DESCRIPTION,
  WARNING,
} from "../../i18n/keysTranslations";
import {
  registerModalLayer,
  unregisterModalLayer,
  getMaxModalLayerZIndex,
} from "../../utils/modalLayer";

const ModalRequestCloseContext = createContext(null);

//eslint-disable-next-line
export const useModalRequestClose = () => {
  const requestClose = useContext(ModalRequestCloseContext);
  if (!requestClose) {
    throw new Error(
      "useModalRequestClose must be used within ModalBasicLayout"
    );
  }
  return requestClose;
};

const ModalActionsSlotContext = createContext(null);

/**
 * Renders its children (usually a DialogActions) in the actions area of the
 * closest ModalBasicLayout, even when it is nested inside another component.
 */
//eslint-disable-next-line
export const ModalActionsPortal = ({ children }) => {
  const actionsSlot = useContext(ModalActionsSlotContext);
  if (!actionsSlot) return null;
  return createPortal(children, actionsSlot);
};

const POPSTATE_CLOSE_REASON = "popstate";

// Global modal stack for nested modals management
let modalStack = [];
let popstateListenerAdded = false;
let ignoreNextPopState = false;

const cleanModalStack = () => {
  modalStack = modalStack.filter((modal) => modal.isOpen);
};

// Global handler for browser back button
const onGlobalPopState = () => {
  if (ignoreNextPopState) {
    ignoreNextPopState = false;
    return;
  }

  const topModal = modalStack[modalStack.length - 1];
  if (topModal && topModal.isOpen) {
    Promise.resolve(topModal.onClose?.(POPSTATE_CLOSE_REASON)).then(
      (result) => {
        if (result === false) return;
        modalStack = modalStack.filter((modal) => modal.id !== topModal.id);
        topModal.hasPushedHistory = false;
      }
    );
  }
};

const addGlobalPopstateListener = () => {
  if (!popstateListenerAdded) {
    window.addEventListener("popstate", onGlobalPopState);
    popstateListenerAdded = true;
  }
};

const generateModalId = () =>
  `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ModalBasicLayout = ({
  children,
  modalOpen,
  onCloseModal,
  title,
  ariaLabel = "Modal génerico",
  maxWidth = "sm",
  fullWidth = false,
  scroll = "paper",
  customComponentTitle,
  fullScreen = false,
  sx = {},
  sxContent = {},
  refContent,
  hasTitle = true,
  titleContent = true,
  sxPaper = {},
  sxTitle = {},
  colorTitle = "black !important",
  variantTitle = "h5",
  description = "",
  variantDescription = "body1",
  sxDescription = {},
  dataTour = "",
  onKeyDown = () => {},
  showCloseButton = true,
  closeButtonSx = {},
  enableHistoryManagement = true,
  historyStateData = { modal: "default" },
  disableScrollLock = false,
  zIndex,
  confirmClose = false,
  topContent = null,
}) => {
  const theme = useTheme();
  const [actionsSlot, setActionsSlot] = useState(null);
  const { t } = useTranslationApp();
  const { totalTopOffsetPx } = useRebrandBanner();

  // Browser history control references
  const modalIdRef = useRef(generateModalId());
  const hasPushedHistoryRef = useRef(false);
  const modalOpenRef = useRef(false);
  const onCloseRef = useRef(onCloseModal);
  const confirmCloseRef = useRef(confirmClose);
  const historyStateRef = useRef(null);
  const isConfirmingCloseRef = useRef(false);
  const requestCloseRef = useRef(async () => {});

  const modalId = modalIdRef.current;
  const resolvedZIndex = modalOpen ? registerModalLayer(modalId, zIndex) : null;
  const [disableEscapeKeyDown, setDisableEscapeKeyDown] = useState(false);

  useEffect(() => {
    if (!modalOpen) {
      unregisterModalLayer(modalId);
    }

    return () => {
      unregisterModalLayer(modalId);
    };
  }, [modalOpen, modalId]);

  const effectiveZIndex = zIndex ?? resolvedZIndex;

  useLayoutEffect(() => {
    if (!modalOpen) {
      setDisableEscapeKeyDown(false);
      return;
    }

    const maxZIndex = getMaxModalLayerZIndex();
    setDisableEscapeKeyDown(
      effectiveZIndex != null && maxZIndex > 0 && effectiveZIndex < maxZIndex
    );
  }, [modalOpen, effectiveZIndex]);

  useEffect(() => {
    const stackedModalId = modalIdRef.current;
    return () => {
      modalStack = modalStack.filter((modal) => modal.id !== stackedModalId);
    };
  }, []);

  useEffect(() => {
    modalOpenRef.current = modalOpen;
    onCloseRef.current = onCloseModal;
    confirmCloseRef.current = confirmClose;
  }, [modalOpen, onCloseModal, confirmClose]);

  // Modal stack and history management
  useEffect(() => {
    if (!enableHistoryManagement) return;

    const modalId = modalIdRef.current;

    if (modalOpen && !hasPushedHistoryRef.current) {
      addGlobalPopstateListener();

      const currentAriaLabel = ariaLabel || "modal";
      const currentHistoryStateData = historyStateData || { modal: "default" };

      const stateData =
        currentHistoryStateData.modal === "default"
          ? { modal: currentAriaLabel, modalId }
          : { ...currentHistoryStateData, modalId };

      window.history.pushState(stateData, "");
      hasPushedHistoryRef.current = true;
      historyStateRef.current = stateData;

      const modalInfo = {
        id: modalId,
        isOpen: true,
        hasPushedHistory: true,
        onClose: (reason) => requestCloseRef.current(reason),
        ariaLabel: currentAriaLabel,
      };

      modalStack.push(modalInfo);
    }

    return () => {
      if (!modalOpen) {
        const modalInStack = modalStack.find((modal) => modal.id === modalId);
        if (modalInStack) {
          modalStack = modalStack.filter((modal) => modal.id !== modalId);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, enableHistoryManagement]);

  // Update modal state in stack
  useEffect(() => {
    const modalInStack = modalStack.find(
      (modal) => modal.id === modalIdRef.current
    );
    if (modalInStack) {
      modalInStack.isOpen = modalOpen;
      modalInStack.onClose = (reason) => requestCloseRef.current(reason);
      modalInStack.hasPushedHistory = hasPushedHistoryRef.current;
    }
  }, [modalOpen, onCloseModal]);

  // Clean up when modal closes
  useEffect(() => {
    if (!modalOpen) {
      const modalInStack = modalStack.find(
        (modal) => modal.id === modalIdRef.current
      );
      if (modalInStack) {
        modalStack = modalStack.filter(
          (modal) => modal.id !== modalIdRef.current
        );
        cleanModalStack();
      }
    }
  }, [modalOpen]);

  const restoreHistoryEntry = useCallback(() => {
    if (!enableHistoryManagement || !historyStateRef.current) return;
    window.history.pushState(historyStateRef.current, "");
  }, [enableHistoryManagement]);

  // Single closer for X, ESC, backdrop, footer, and browser back.
  // confirmClose shows the shared warning; onCloseModal may still return
  // false (e.g. image/template edit).
  const requestClose = useCallback(
    async (reason) => {
      if (isConfirmingCloseRef.current) return false;

      if (confirmCloseRef.current) {
        isConfirmingCloseRef.current = true;
        try {
          const confirmation = await ConfirmAlert({
            title: t(WARNING),
            text: t(MODAL_CLOSE_UNSAVED_WARNING_DESCRIPTION),
            confirmButtonText: t(MODAL_CLOSE_UNSAVED_CONFIRM),
            cancelButtonText: t(BUTTON_CANCEL),
          });
          if (!confirmation?.isConfirmed) {
            if (reason === POPSTATE_CLOSE_REASON) {
              restoreHistoryEntry();
            }
            return false;
          }
        } finally {
          isConfirmingCloseRef.current = false;
        }
      }

      const result = await Promise.resolve(onCloseRef.current?.(reason));
      if (result === false) {
        if (reason === POPSTATE_CLOSE_REASON) {
          restoreHistoryEntry();
        }
        return false;
      }

      if (reason === POPSTATE_CLOSE_REASON) {
        modalStack = modalStack.filter(
          (modal) => modal.id !== modalIdRef.current
        );
        hasPushedHistoryRef.current = false;
        return;
      }

      if (enableHistoryManagement && hasPushedHistoryRef.current) {
        modalStack = modalStack.filter(
          (modal) => modal.id !== modalIdRef.current
        );
        hasPushedHistoryRef.current = false;
        ignoreNextPopState = true;
        window.history.back();
      }
    },
    [enableHistoryManagement, restoreHistoryEntry, t]
  );

  requestCloseRef.current = requestClose;

  const actions = [];
  const content = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === DialogActions) {
        actions.push(child);
      } else {
        content.push(child);
      }
    }
  });

  const titleDescriptionBlock =
    (title && titleContent) || description ? (
      <Box sx={{ mb: 2 }}>
        {title && titleContent && (
          <Typography
            variant={variantTitle}
            color="inherit"
            align="left"
            sx={{
              fontWeight: 600,
              ...sxTitle,
            }}
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            variant={variantDescription}
            align="left"
            sx={{
              ...sxDescription,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    ) : null;

  const modalBody = (
    <>
      <DialogContent
        sx={{
          px: {
            xs: 3,
            sm: 5,
          },
          pb: 2,
          ...sxContent,
        }}
        ref={refContent}
      >
        {topContent && <Box sx={{ mb: 2 }}>{topContent}</Box>}
        {titleDescriptionBlock}
        {content}
      </DialogContent>
      {actions}
      <div ref={setActionsSlot} />
    </>
  );

  return (
    <ModalRequestCloseContext.Provider value={requestClose}>
      <ModalActionsSlotContext.Provider value={actionsSlot}>
        <Dialog
          open={modalOpen}
          onClose={(e, reason) => {
            e.stopPropagation();
            requestClose(reason);
          }}
          disableEscapeKeyDown={disableEscapeKeyDown}
          disableScrollLock={disableScrollLock}
          maxWidth={maxWidth}
          fullWidth={fullWidth}
          fullScreen={fullScreen}
          scroll={scroll}
          aria-labelledby={ariaLabel}
          aria-describedby={ariaLabel}
          sx={{
            ...(effectiveZIndex !== undefined && effectiveZIndex !== null
              ? { zIndex: effectiveZIndex }
              : {}),
            "& .MuiDialog-paper": {
              backgroundImage: "none",
              backgroundColor: "background.paper",
              m: 0,
              maxHeight: fullScreen ? "100%" : "calc(100% - 8%)",
              borderRadius: fullScreen ? 0 : 4,
              width: fullScreen
                ? "100%"
                : {
                    xs: "calc(100% - 24px)",
                    sm: `calc(100% - ${totalTopOffsetPx}px)`,
                  },
              ...sxPaper,
            },
            ...sx,
          }}
          PaperProps={{
            "data-tour": dataTour,
          }}
          onKeyDown={onKeyDown}
        >
          {hasTitle && (
            <DialogTitle
              sx={{
                m: 0,
                px: 2,
                pb: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                ...sxTitle,
              }}
              component="div"
            >
              {title && !titleContent && (
                <Typography
                  variant={variantTitle}
                  color="inherit"
                  align="left"
                  sx={{ fontWeight: 600, color: `${colorTitle} !important` }}
                >
                  {title}
                </Typography>
              )}
              {customComponentTitle}
              {showCloseButton && (
                <IconButton
                  className="close-modal-button"
                  aria-label="close"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestClose();
                  }}
                  sx={{
                    p: 0.5,
                    fontSize: "1.4rem",
                    color: "background.paperSecondaryContrastText",
                    backgroundColor: "background.paperSecondary",
                    "&:hover": {
                      color: "background.paperSecondaryContrastText",
                      backgroundColor: "background.paperSecondary",
                      filter: theme.palette.hover.main,
                    },
                    ...closeButtonSx,
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              )}
            </DialogTitle>
          )}

          {modalBody}
        </Dialog>
      </ModalActionsSlotContext.Provider>
    </ModalRequestCloseContext.Provider>
  );
};

export default ModalBasicLayout;
